import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, Navigate } from 'react-router';
import { Icon } from '../../components/icons';
import { ErrorNote, Loading, Modal, cx } from '../../components/ui';
import { api, type AdminOverview, type Company } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDateTime, formatMoney, monthName } from '../../lib/format';
import { AButton, AField, AINPUT, ATD, ATH, ATR, AdminShell, Panel, Tile } from './shared';

/** Every company's month in one view, and the door to each. */
export function AdminOverviewPage() {
  const { user } = useAuth();
  const q = useQuery({ queryKey: ['admin', 'overview'], queryFn: () => api.get<AdminOverview>('/api/admin/overview'), refetchInterval: 30_000, enabled: !!user?.platformAdmin });
  const [creating, setCreating] = useState(false);
  if (!user?.platformAdmin) return <Navigate to="/" replace />;
  const d = q.data;
  return (
    <AdminShell
      title={d ? monthName(d.period.year, d.period.month) : 'This month'}
      subtitle="What every company used, what it will be billed, and what is still owed. Counts and money only: a company's candidates stay its own."
      actions={
        <AButton primary onClick={() => setCreating(true)}>
          <Icon name="plus" className="h-4 w-4" /> New company
        </AButton>
      }
    >
      <ErrorNote error={q.error} />
      {q.isLoading && <Loading />}
      {d && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <Tile label="Companies" value={d.totals.active} note={`${d.totals.companies} in all${d.totals.companies - d.totals.active ? `, ${d.totals.companies - d.totals.active} suspended` : ''}`} />
            <Tile label="Interviews this month" value={d.totals.interviews} note={`${d.totals.minutes} minutes of AI time`} />
            <Tile label="Live now" value={d.totals.liveNow} tone={d.totals.liveNow ? 'good' : 'default'} note="across every company" />
            <Tile label="Expected this month" value={formatMoney(d.totals.expected)} tone="brand" note="plans plus usage, active companies" />
            <Tile label="Outstanding" value={formatMoney(d.totals.outstanding)} tone={d.totals.outstanding ? 'warn' : 'default'} note={`${d.totals.outstandingCount} invoice${d.totals.outstandingCount === 1 ? '' : 's'} issued, unpaid`} />
            <Tile label="Paid this month" value={formatMoney(d.totals.paidThisMonth)} tone="good" />
          </div>

          <Panel title="Companies" description="This month's usage against each plan. Open a company to set its plan, raise its invoice or add an account." padding={false}>
            <div className="scrollbar-thin overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead>
                  <tr>
                    <th className={ATH}>Company</th>
                    <th className={ATH}>Plan</th>
                    <th className={cx(ATH, 'text-right')}>Interviews</th>
                    <th className={cx(ATH, 'w-56')}>Of included</th>
                    <th className={cx(ATH, 'text-right')}>Minutes</th>
                    <th className={cx(ATH, 'text-right')}>This month</th>
                    <th className={ATH}>Team</th>
                    <th className={ATH} />
                  </tr>
                </thead>
                <tbody>
                  {d.companies.map((c) => {
                    const pct = c.plan.includedInterviews ? Math.min(100, (c.month.interviews / c.plan.includedInterviews) * 100) : 0;
                    const over = c.plan.includedInterviews > 0 && c.month.interviews > c.plan.includedInterviews;
                    return (
                      <tr key={c.id} className={ATR}>
                        <td className={ATD}>
                          <Link to={`/admin/companies/${c.id}`} className="font-semibold hover:text-[#f7a6cf]">
                            {c.name}
                          </Link>
                          <div className="mt-0.5 flex items-center gap-2 text-xs text-white/45">
                            {c.id === user.orgId && <span>yours</span>}
                            {!c.active && <span className="rounded-full bg-[#fbbf24]/15 px-1.5 py-px font-mono text-[10px] text-[#fbbf24]">suspended</span>}
                            <span>since {formatDateTime(c.createdAt).split(',')[0]}</span>
                          </div>
                        </td>
                        <td className={ATD}>
                          <div className="font-medium">{c.plan.planName}</div>
                          <div className="font-mono text-[11px] text-white/45">
                            {formatMoney(c.plan.monthlyFee)}/mo · {c.plan.includedInterviews} incl · {formatMoney(c.plan.pricePerInterview)} extra
                          </div>
                        </td>
                        <td className={cx(ATD, 'text-right font-mono tabular')}>{c.month.interviews}</td>
                        <td className={ATD}>
                          <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
                            <div className={cx('h-full rounded-full', over ? 'bg-[#fbbf24]' : 'bg-brand-gradient')} style={{ width: `${pct}%` }} />
                          </div>
                          <div className="mt-1 font-mono text-[10px] text-white/45">
                            {c.plan.includedInterviews ? `${c.month.interviews} of ${c.plan.includedInterviews}${over ? ` · ${c.quote.extraInterviews} extra` : ''}` : 'pay as you go'}
                          </div>
                        </td>
                        <td className={cx(ATD, 'text-right font-mono tabular')}>{c.month.minutes}</td>
                        <td className={cx(ATD, 'text-right font-mono tabular font-semibold')}>{formatMoney(c.quote.total)}</td>
                        <td className={cx(ATD, 'font-mono text-xs text-white/60')}>
                          {c.users} people · {c.jobs} jobs
                        </td>
                        <td className={cx(ATD, 'text-right')}>
                          <Link to={`/admin/companies/${c.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[#f7a6cf] hover:underline">
                            Open <Icon name="arrowRight" className="h-3.5 w-3.5" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      )}
      <NewCompanyDialog open={creating} onClose={() => setCreating(false)} />
    </AdminShell>
  );
}

const PRESETS: Array<{ id: string; label: string; plan: Company['plan'] }> = [
  { id: 'pilot', label: 'Pilot: free, 25 interviews a month', plan: { planName: 'Pilot', includedInterviews: 25, pricePerInterview: 0, monthlyFee: 0 } },
  { id: 'starter', label: 'Starter: ₹4,999 a month, 50 interviews, ₹120 each after', plan: { planName: 'Starter', includedInterviews: 50, pricePerInterview: 12_000, monthlyFee: 499_900 } },
  { id: 'growth', label: 'Growth: ₹14,999 a month, 200 interviews, ₹90 each after', plan: { planName: 'Growth', includedInterviews: 200, pricePerInterview: 9_000, monthlyFee: 1_499_900 } },
  { id: 'payg', label: 'Pay as you go: ₹150 an interview', plan: { planName: 'Pay as you go', includedInterviews: 0, pricePerInterview: 15_000, monthlyFee: 0 } },
];

export function NewCompanyDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: '', adminName: '', adminEmail: '', adminPassword: '', preset: 'pilot' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);
  const reset = () => {
    setForm({ name: '', adminName: '', adminEmail: '', adminPassword: '', preset: 'pilot' });
    setDone(null);
    setError(null);
    onClose();
  };
  return (
    <Modal open={open} onClose={reset} title="New company" description="Creates the company, its first admin and its plan. That admin adds the rest of the team under Settings." footer={done ? <AButton primary onClick={reset}>Done</AButton> : undefined}>
      {done ? (
        <p className="text-sm text-fg">{done}</p>
      ) : (
        <form
          className="grid gap-4 sm:grid-cols-2"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError(null);
            try {
              const plan = PRESETS.find((p) => p.id === form.preset)!.plan;
              await api.post('/api/orgs', { name: form.name, admin: { name: form.adminName, email: form.adminEmail, password: form.adminPassword }, plan });
              await qc.invalidateQueries({ queryKey: ['admin'] });
              await qc.invalidateQueries({ queryKey: ['companies'] });
              setDone(`${form.name} is ready on the ${plan.planName} plan. Send ${form.adminEmail} the sign-in address and the temporary password.`);
            } catch (err) {
              setError(err);
            } finally {
              setBusy(false);
            }
          }}
        >
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-[13px] font-semibold text-fg">Company name</span>
            <input required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="block h-10 w-full rounded-sm bg-surface px-3.5 text-sm text-fg ring-1 ring-inset ring-border-strong focus:outline-none focus:ring-2 focus:ring-accent" />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[13px] font-semibold text-fg">First admin's name</span>
            <input required minLength={2} value={form.adminName} onChange={(e) => setForm({ ...form, adminName: e.target.value })} className="block h-10 w-full rounded-sm bg-surface px-3.5 text-sm text-fg ring-1 ring-inset ring-border-strong focus:outline-none focus:ring-2 focus:ring-accent" />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[13px] font-semibold text-fg">Admin's email</span>
            <input required type="email" value={form.adminEmail} onChange={(e) => setForm({ ...form, adminEmail: e.target.value })} className="block h-10 w-full rounded-sm bg-surface px-3.5 text-sm text-fg ring-1 ring-inset ring-border-strong focus:outline-none focus:ring-2 focus:ring-accent" />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[13px] font-semibold text-fg">Temporary password</span>
            <input required minLength={10} type="password" autoComplete="new-password" value={form.adminPassword} onChange={(e) => setForm({ ...form, adminPassword: e.target.value })} className="block h-10 w-full rounded-sm bg-surface px-3.5 text-sm text-fg ring-1 ring-inset ring-border-strong focus:outline-none focus:ring-2 focus:ring-accent" />
            <span className="mt-1.5 block text-xs text-muted-fg">At least 10 characters.</span>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[13px] font-semibold text-fg">Plan</span>
            <select value={form.preset} onChange={(e) => setForm({ ...form, preset: e.target.value })} className="block h-10 w-full rounded-sm bg-surface px-3.5 text-sm text-fg ring-1 ring-inset ring-border-strong focus:outline-none focus:ring-2 focus:ring-accent">
              {PRESETS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
            <span className="mt-1.5 block text-xs text-muted-fg">Change any figure later on the company's page.</span>
          </label>
          <div className="sm:col-span-2">
            <ErrorNote error={error} />
          </div>
          <div className="flex justify-end gap-2 sm:col-span-2">
            <AButton onClick={reset} className="!bg-muted !text-fg !ring-border-strong">
              Cancel
            </AButton>
            <AButton primary type="submit" loading={busy}>
              Create company
            </AButton>
          </div>
        </form>
      )}
    </Modal>
  );
}

/** The plain list, for the Companies tab. */
export function AdminCompaniesPage() {
  const { user } = useAuth();
  const q = useQuery({ queryKey: ['companies'], queryFn: () => api.get<Company[]>('/api/orgs'), enabled: !!user?.platformAdmin });
  const [creating, setCreating] = useState(false);
  if (!user?.platformAdmin) return <Navigate to="/" replace />;
  return (
    <AdminShell
      title="Companies"
      subtitle="Every company on the platform, with its plan and the last 30 days. Suspend one from its page; its people are signed out at once."
      actions={
        <AButton primary onClick={() => setCreating(true)}>
          <Icon name="plus" className="h-4 w-4" /> New company
        </AButton>
      }
    >
      <ErrorNote error={q.error} />
      {q.isLoading && <Loading />}
      {q.data && (
        <Panel padding={false}>
          <div className="scrollbar-thin overflow-x-auto">
            <table className="w-full min-w-[820px]">
              <thead>
                <tr>
                  <th className={ATH}>Company</th>
                  <th className={ATH}>Plan</th>
                  <th className={cx(ATH, 'text-right')}>Team</th>
                  <th className={cx(ATH, 'text-right')}>Jobs</th>
                  <th className={cx(ATH, 'text-right')}>Candidates</th>
                  <th className={cx(ATH, 'text-right')}>Interviews, 30 days</th>
                  <th className={ATH}>Since</th>
                  <th className={ATH} />
                </tr>
              </thead>
              <tbody>
                {q.data.map((c) => (
                  <tr key={c.id} className={ATR}>
                    <td className={ATD}>
                      <Link to={`/admin/companies/${c.id}`} className="font-semibold hover:text-[#f7a6cf]">
                        {c.name}
                      </Link>
                      {!c.active && <span className="ml-2 rounded-full bg-[#fbbf24]/15 px-1.5 py-px font-mono text-[10px] text-[#fbbf24]">suspended</span>}
                      {c.id === user.orgId && <span className="ml-2 text-xs text-white/45">yours</span>}
                    </td>
                    <td className={ATD}>{c.plan.planName}</td>
                    <td className={cx(ATD, 'text-right font-mono tabular')}>{c.users}</td>
                    <td className={cx(ATD, 'text-right font-mono tabular')}>{c.jobs}</td>
                    <td className={cx(ATD, 'text-right font-mono tabular')}>{c.candidates}</td>
                    <td className={cx(ATD, 'text-right font-mono tabular')}>
                      {c.last30Days.interviews} <span className="text-white/45">· {c.last30Days.minutes} min</span>
                    </td>
                    <td className={cx(ATD, 'text-xs text-white/55')}>{formatDateTime(c.createdAt)}</td>
                    <td className={cx(ATD, 'text-right')}>
                      <Link to={`/admin/companies/${c.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[#f7a6cf] hover:underline">
                        Open <Icon name="arrowRight" className="h-3.5 w-3.5" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
      <NewCompanyDialog open={creating} onClose={() => setCreating(false)} />
    </AdminShell>
  );
}

// Keep the console's inputs referenced for the company page's forms.
export { AField, AINPUT };
